import { useState } from "react";
import { motion } from "framer-motion";
import { useMatch } from "@/match/MatchContext";
import { WindowBar } from "./SearchSection";
import {
  PENALTIES,
  CLOSING_LINE,
  SHOOTOUT_QUOTE,
  SHOOTOUT_SUMMARY_LINE,
  SHOOTOUT_PENDING_LINE,
  PROJECTS,
  PORTFOLIO_HEADING,
  PORTFOLIO_HEADER_NOTE,
  PORTFOLIO_PITCH,
  PORTFOLIO_BUTTONS,
} from "@/data/content";

// Card image with coloured-panel fallback if the file is missing.
function PortfolioImage({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={alt || ""}
        className="w-full h-full object-cover"
        onError={() => setFailed(true)}
      />
    );
  }
  return null;
}

function OutcomeSummary({ results }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="outcome-summary">
      {PENALTIES.map((p, i) => {
        const r = results[i];
        return (
          <div
            key={p.id}
            className={`border-[3px] border-black p-3 text-center ${
              r === "advaith" ? "bg-[#84E116]" : r === "life" ? "bg-[#FF3B30]" : "bg-white"
            }`}
          >
            <span className="block font-broadcast font-extrabold text-[9px] tracking-[0.2em] uppercase text-black/70 mb-1">
              Pen {i + 1}
            </span>
            <span className={`block font-display uppercase text-xs sm:text-sm ${r === "life" ? "text-white" : "text-black"}`}>
              {r === undefined ? "—" : r === "advaith" ? "GOAL" : "SAVED"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function FinalResult() {
  const { shootout, restartShootout } = useMatch();
  const results = shootout.results;
  const complete = results.length === PENALTIES.length;
  const advaithGoals = results.filter((r) => r === "advaith").length;
  const lifeGoals = results.filter((r) => r === "life").length;

  return (
    <div className="bg-[#F2F2F0] py-16 sm:py-24 px-4 sm:px-8 lg:px-16" data-testid="final-result-section">
      <div className="max-w-[1500px] mx-auto">
        {/* score + closing line */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 items-end mb-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.65 }}
          >
            <span className="inline-block bg-[#0A0A0A] text-[#84E116] font-broadcast font-bold text-[10px] sm:text-xs tracking-[0.3em] uppercase px-4 py-2 mb-6">
              Full time · After penalties
            </span>
            <div
              className="font-display uppercase leading-[0.85] tracking-tight text-[#0052FF] text-[26vw] sm:text-[9rem] lg:text-[11rem]"
              data-testid="final-score"
            >
              {advaithGoals}–{lifeGoals}
            </div>
            <div className="mt-4 flex items-center gap-4 font-broadcast font-extrabold text-xs sm:text-sm tracking-[0.25em] uppercase text-black">
              <span>Advaith</span>
              <span className="text-black/40">vs</span>
              <span>Life</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="bg-white border-[3px] border-black hard-shadow-lg p-6 sm:p-8 mb-6">
              <p className="font-display uppercase text-xl sm:text-2xl lg:text-3xl leading-tight tracking-tight text-black" data-testid="closing-line">
                "{complete ? SHOOTOUT_QUOTE : CLOSING_LINE}"
              </p>
              {!complete ? (
                <p className="mt-4 font-broadcast font-bold text-[10px] tracking-[0.2em] uppercase text-black/50">
                  {SHOOTOUT_PENDING_LINE}
                </p>
              ) : (
                <p className="mt-4 font-broadcast font-bold text-[10px] tracking-[0.2em] uppercase text-black/50">
                  {SHOOTOUT_SUMMARY_LINE}
                </p>
              )}
            </div>
            <OutcomeSummary results={results} />
            <button
              type="button"
              onClick={restartShootout}
              data-testid="replay-shootout-btn"
              className="mt-6 font-display uppercase text-base sm:text-lg tracking-wide bg-white text-black border-[3px] border-black hard-shadow px-8 py-3.5 transition-all duration-200 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#000] active:translate-x-1 active:translate-y-1 active:shadow-none"
            >
              Replay The Shootout
            </button>
          </motion.div>
        </div>

        {/* portfolio showcase */}
        <div className="border-t-4 border-black pt-12" data-testid="portfolio-area">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
            <h3 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight text-black">
              {PORTFOLIO_HEADING}
            </h3>
            <span className="font-broadcast font-bold text-[10px] sm:text-xs tracking-[0.25em] uppercase text-black/50">
              {PORTFOLIO_HEADER_NOTE}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-6">
            {PROJECTS.map((project, i) => (
              <motion.a
                key={project.id}
                href={project.link}
                {...(project.link?.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid={`project-card-${project.id}`}
                className="block bg-white border-[3px] border-black hard-shadow-lg overflow-hidden transition-transform duration-200 hover:-translate-y-1.5"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <WindowBar />
                <div
                  className="aspect-[16/10] flex items-center justify-center border-b-[3px] border-black"
                  style={{ backgroundColor: project.accent }}
                >
                  <PortfolioImage src={project.imageUrl} alt={project.imageAlt} />
                </div>
                <div className="p-5">
                  <h4 className="font-broadcast font-extrabold text-xs sm:text-sm tracking-[0.18em] uppercase text-black mb-3">
                    {project.title}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="border-2 border-black font-broadcast font-bold text-[9px] tracking-[0.2em] uppercase px-2.5 py-1 text-black"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.a>
            ))}
          </div>

          <p className="text-sm sm:text-base font-medium leading-relaxed text-black/70 mb-10">
            {PORTFOLIO_PITCH}
          </p>

          <div className="flex flex-wrap gap-4">
            {PORTFOLIO_BUTTONS.map((btn, i) => (
              <a
                key={`${btn.label}-${i}`}
                href={btn.href}
                {...(btn.href?.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid={btn.primary ? "contact-btn" : `profile-link-${btn.label.toLowerCase()}-${i}`}
                className={`font-display uppercase text-sm sm:text-base tracking-wide border-[3px] border-black hard-shadow px-7 py-3 transition-all duration-200 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#000] ${
                  btn.primary ? "bg-[#0052FF] text-white hover:bg-[#0041CC]" : "bg-white text-black"
                }`}
              >
                {btn.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
